/**
 * User and Account type definitions
 */

export interface User {
  id: string;
  email: string;
  created_at?: string;
  updated_at?: string;
  email_confirmed_at?: string;
  last_sign_in_at?: string;
  app_metadata?: Record<string, unknown>;
  user_metadata?: UserMetadata;
}

export interface UserMetadata {
  name?: string;
  lastname?: string;
  company?: string;
  phone?: string;
}

type AccountType = 'individual' | 'team';
export type AccountStatus = 'active' | 'expired' | 'pending' | 'revoked';
export interface Account {
  id: string;
  email: string;
  name: string | null;
  lastname: string | null;
  phone: string | null;
  company_id: string | null;
  subscription_monthly_minutes: number | null;
  subscription_plan_id: string | null;
  subscription_status: SubscriptionStatus | null;
  created_at?: string;
  updated_at?: string;
  is_admin?: boolean;
  isAdmin?: boolean;
  role?: UserRole;
  slack_connected?: boolean;
  fub_connected?: boolean;
  suresend_connected?: boolean;
  suresend_account_name?: string | null;
  suresend_account_email?: string | null;
  suresend_token_scope?: string | null;
  zoom_connected?: boolean;
  stripe_customer_id?: string | null;
  stripe_subscription_id?: string | null;
  minutes_balance?: number | null;
  subscription_current_period_end?: string | null;
  subscription_created_at?: string | null;
  last_invoice_paid_at?: string | null;
  subscription_cancelled_at?: string | null;
  subscription_updated_at?: string | null;
  stripe_price_id?: string | null;
  subscription_current_period_start?: string | null;
  account_type: AccountType;
  status: AccountStatus;
  has_referral_discount?: boolean;
}
export interface AccountUsage {
  planMinutes: number;
  remainingMinutes: number;
  rolloverMinutes: number;
  usedMinutes: number;
  periodEnd: string | null;
}

export type SubscriptionStatus =
  | 'active'
  | 'canceled'
  | 'incomplete'
  | 'incomplete_expired'
  | 'past_due'
  | 'trialing'
  | 'unpaid'
  | 'paused';

export const USER_ROLES = ['owner', 'admin', 'user', 'superadmin'] as const;

export type UserRole = typeof USER_ROLES[number];

export const TEAM_ADMIN_ROLES: readonly UserRole[] = ['owner', 'admin', 'superadmin'];

/** Body of POST /accounts/signup — the server creates the auth user AND the domain rows. */
export interface SignupData {
  email: string;
  password: string;
  name: string;
  lastname: string;
  company?: string;
  phone?: string;
  invite_token?: string;
  team_invite_token?: string;
}

/** Error codes POST /accounts/signup returns. `EMAIL_TAKEN` is the one the UI must
 *  distinguish, since it is also what a retry after a failed sign-in looks like. */
export type SignupErrorCode =
  | 'EMAIL_TAKEN'
  | 'INVALID_INPUT'
  | 'INVITE_INVALID'
  | 'INVITE_CLAIMED'
  | 'INVITE_EMAIL_MISMATCH'
  | 'INVITATION_INVALID'
  | 'INVITATION_CLAIMED';

export interface UpdateAccountData {
  id?: string;
  name?: string;
  lastname?: string;
  company_id?: string;
  subscription_plan_id?: string;
  subscription_status?: SubscriptionStatus;
  subscription_monthly_minutes?: number;
  phone?: string | null;
}

export interface SignUpData {
  email: string;
  password: string;
  options?: {
    data?: {
      name?: string;
      lastname?: string;
      company?: string;
      phone?: string;
      invite_token?: string;
    };
  };
}

export interface SignInData {
  email: string;
  password: string;
}

export interface AuthResult {
  data: {
    user: User | null;
    session: Session | null;
  };
  error: AuthError | null;
}

export interface Session {
  access_token: string;
  refresh_token: string;
  expires_in: number;
  expires_at: number;
  token_type: string;
  user: User;
}

export interface AuthError {
  message: string;
  status?: number;
  code?: string;
}
